import './App.css';
import { useState, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate
} from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import Loader from './Components/Loader';
import Home from './Home/Home';
import LandingPage from './LandingPage/LandingPage';
import { ApiGatewayS3Function } from './UserConfigs/ApiGatewayFunction.js';
import { createBrand, deleteBrand, updateBrandIdentity, updateBrandReferences, updateBrandOffers, updateCredits } from './lib/brands/createBrand.js';
import { diffObject } from './lib/brands/diffObject.js';
import { createAsset } from './lib/generate/createAsset.js';
import { updateAsset } from './lib/generate/updateAsset.js';
import { DISPLAY } from './constants.js';
import Brands from './Brands/Brands';
import NewBrand from './Brands/NewBrand';
import BrandDetail from './Brands/BrandDetail';
import Assets from './Assets/Assets';
import Usage from './Usage/Usage';
import Generate from './Generate/Generate';
import Compose from './Compose/Compose';
import TagManager from 'react-gtm-module';

function App({ testingState }) {
  const { user, isAuthenticated, isLoading } = useAuth0();
  const [userConfig, setUserConfig] = useState(null);
  const [configResponse, setConfigResponse] = useState(null);
  const [isConfigLoading, setIsConfigLoading] = useState(false);
  const [configError, setConfigError] = useState(null);

  // Save-a-brand state — separate from isConfigLoading above (that's the
  // initial fetch on login; this is a specific brand-save request). Kept
  // named distinctly so the two are never confused for one another.
  const [isSavingBrand, setIsSavingBrand] = useState(false);
  const [saveBrandApiResponse, setSaveBrandApiResponse] = useState(null);

  const [isSavingReferences, setIsSavingReferences] = useState(false);
  const [saveReferencesApiResponse, setSaveReferencesApiResponse] = useState(null);

  const [isSavingOffers, setIsSavingOffers] = useState(false);
  const [saveOffersApiResponse, setSaveOffersApiResponse] = useState(null);

  const [isCreatingAsset, setIsCreatingAsset] = useState(false);
  const [createAssetApiResponse, setCreateAssetApiResponse] = useState(null);

  const [isSavingAsset, setIsSavingAsset] = useState(false);
  const [saveAssetApiResponse, setSaveAssetApiResponse] = useState(null);

  const tagManagerArgs = {
    gtmId: 'GTM-N956Z6GW',
  } 

  TagManager.initialize(tagManagerArgs);

  useEffect(() => {
    if (!user) return;

    const userProfile = user;
    const userEmail = user.email;
    const userName = userProfile.name;
    const userNickname = userProfile.nickname;
    const userRole = userProfile?.stack_user_metadata?.role;
    const support_id = userProfile?.stack_user_metadata?.studio_id;
    const support_key = userProfile?.stack_user_metadata?.studio_key;
    const patternAPI = userProfile?.stack_user_metadata?.pattern_api;
    const clientId = userProfile?.stack_user_metadata?.clientId;

    const userBase = {
      userEmail,
      userName,
      userNickname,
      userRole,
      support_id,
      support_key,
      patternAPI,
      clientId,
      testingState
    };
    setUserConfig(userBase);

    const key = `stack-studio/clients%2F${clientId}.json`;
    const event_data = { request_type: 'preagg' };

    setIsConfigLoading(true);
    setConfigError(null);

    ApiGatewayS3Function(
      event_data,
      patternAPI,
      support_key,
      support_id,
      setIsConfigLoading,
      setConfigResponse,
      testingState,
      key,
      'preagg'
    );
  }, [user, testingState]);

  const stillWaitingOnConfig = isAuthenticated && (!userConfig || !configResponse || isConfigLoading);
  const showLoader = isLoading || stillWaitingOnConfig;

  // Merges a change into the ONE matching brand entry, by brand.id -
  // the client-side counterpart to the backend's own new targeted-patch
  // contract. Unlike the old handleBrandConfigChange (which replaced
  // the entire array with whatever the caller had computed locally -
  // the exact mechanism that let a stale tab silently erase brands it
  // didn't know about), this only ever touches the one matching entry,
  // leaving every other brand in local state completely untouched.
  // Shared by patchBrandEntry/addBrandEntry below - filters out any
  // array entry missing .brand (which would otherwise crash every
  // downstream .brand.id access), logging the entry's own raw content
  // and index so a genuinely corrupt entry in the real config data is
  // directly inspectable from the console, not just counted.
  function filterMalformedBrands(rawList, context) {
    const malformed = [];
    const list = rawList.filter((item, i) => {
      if (item?.brand) return true;
      malformed.push({ index: i, value: item });
      return false;
    });
    if (malformed.length > 0) {
      console.warn(
        `${context}: found ${malformed.length} malformed entr${
          malformed.length === 1 ? "y" : "ies"
        } in the brand config (missing .brand) - filtered out to avoid a crash. Raw content:`,
        malformed
      );
    }
    return list;
  }

  function patchBrandEntry(brandId, updater) {
    setConfigResponse((prev) => {
      if (!prev) return prev;
      const list = filterMalformedBrands(prev.data.body.config ?? [], "patchBrandEntry");
      const nextConfig = list.map((item) => (item.brand.id === brandId ? updater(item) : item));
      return {
        ...prev,
        data: { ...prev.data, body: { ...prev.data.body, config: nextConfig } },
      };
    });
  }

  // Appends a newly-created brand entry to local state, or replaces the
  // placeholder entry (brand.id === null) if one exists - same logic
  // the backend itself applies, kept in sync deliberately so local
  // state and server state never diverge on this specific rule.
  function addBrandEntry(entry) {
    setConfigResponse((prev) => {
      if (!prev) return prev;
      const list = filterMalformedBrands(prev.data.body.config ?? [], "addBrandEntry");
      const placeholderIndex = list.findIndex((item) => item.brand.id === null);
      const nextConfig =
        placeholderIndex !== -1
          ? list.map((item, i) => (i === placeholderIndex ? entry : item))
          : [...list, entry];
      return {
        ...prev,
        data: { ...prev.data, body: { ...prev.data.body, config: nextConfig } },
      };
    });
  }

  // Removes one brand entry from local state, by brand.id - the
  // symmetric counterpart to addBrandEntry above.
  function removeBrandEntry(brandId) {
    setConfigResponse((prev) => {
      if (!prev) return prev;
      const list = filterMalformedBrands(prev.data.body.config ?? [], "removeBrandEntry");
      const nextConfig = list.filter((item) => item.brand.id !== brandId);
      return {
        ...prev,
        data: { ...prev.data, body: { ...prev.data.body, config: nextConfig } },
      };
    });
  }

  // The one place in the app that deletes a brand. Reuses the same
  // isSavingBrand/saveBrandApiResponse state as creation/identity saves
  // above - same category of brand-level mutation, no need for a
  // separate, dedicated loading state just for this.
  async function handleDeleteBrand(brandId) {
    await deleteBrand(configArray, brandId, setIsSavingBrand, setSaveBrandApiResponse);
    removeBrandEntry(brandId);
  }

  // Brand creation is no longer local-only (that was the old "agreed
  // flow", from back when the backend was never actually touched at
  // creation time at all). The backend now generates the real brand
  // id/slug immediately, so a freshly-created brand is genuinely
  // persisted the moment this resolves, not just held in local state
  // until a separate, easy-to-miss Save step on the identity tab.
  async function handleCreateBrand(name, websiteUrl) {
    const { brand, entry } = await createBrand(
      configArray,
      { name, websiteUrl },
      setIsSavingBrand,
      setSaveBrandApiResponse
    );
    addBrandEntry(entry);
    return brand;
  }

  // The ONE place in the app that pushes identity edits to the backend.
  // Called from BrandDetail's Save button — nowhere else calls this.
  //
  // Sends only the actual diff (via diffObject), not the full local
  // identity - the real fix for a confirmed bug: sending the whole
  // object every time meant an unrelated, unchanged-but-stale sub-field
  // (e.g. fonts.heading, when only fonts.body was actually edited in
  // this tab) would silently overwrite a genuine, concurrent change
  // someone else made to that same sub-field in the meantime. The
  // backend's own deep-merge (see brand_config_handler.py) only helps
  // if the client actually sends a partial patch - this is the other
  // half of that fix. Local state still updates with the full new
  // identity, since the person's own local view should reflect their
  // complete edit, regardless of what was minimally sent to the backend.
  async function handleSaveBrand(brandId, identity) {
    const current = (configArray.config ?? []).find((item) => item.brand.id === brandId);
    const patch = diffObject(current?.identity ?? {}, identity);
    await updateBrandIdentity(configArray, { brandId, identity: patch }, setIsSavingBrand, setSaveBrandApiResponse);
    patchBrandEntry(brandId, (item) => ({ ...item, identity, brand: { ...item.brand, status: "active" } }));
  }

  // Same shape as handleSaveBrand — the one place references get pushed
  // to the backend. Called after ReferencesTab has already uploaded (or
  // deleted) the underlying S3 file itself.
  async function handleSaveReferences(brandId, references) {
    await updateBrandReferences(
      configArray,
      { brandId, references },
      setIsSavingReferences,
      setSaveReferencesApiResponse
    );
    patchBrandEntry(brandId, (item) => ({ ...item, references }));
  }

  // Same shape again — offers have no file involved, so the caller just
  // computes the next offers array locally (add/edit/toggle/remove) and
  // this persists it. No separate upload step needed, unlike references.
  async function handleSaveOffers(brandId, offers) {
    await updateBrandOffers(configArray, { brandId, offers }, setIsSavingOffers, setSaveOffersApiResponse);
    patchBrandEntry(brandId, (item) => ({ ...item, offers }));
  }

  // Credits live at the top level of configResponse, not inside a brand
  // entry — separate updater from handleBrandConfigChange since it's a
  // different field.
  function handleCreditsChange(nextCredits) {
    setConfigResponse((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        data: {
          ...prev.data,
          body: {
            ...prev.data.body,
            credits: nextCredits,
          },
        },
      };
    });
  }

  // Creates an asset via the dedicated assets store, then deducts
  // credits via updateCredits (a targeted, top-level patch, not the old
  // full-array-overwrite saveBrandConfig this used to reuse). Still
  // client-computed, per the already-accepted POC tradeoff (no
  // atomicity guarantee the way the original's DB transaction has) -
  // this fix addresses the brand-config race condition specifically,
  // not credits' own, separately-known lack of one.
  async function handleCreateAsset(brandId, recipe, content) {
    const asset = await createAsset(
      configArray,
      { brandId, recipe, content },
      setIsCreatingAsset,
      setCreateAssetApiResponse
    );

    const nextCredits = configArray.credits - DISPLAY.CREDITS.BANNER_SET;
    await updateCredits(configArray, nextCredits, setIsCreatingAsset, setCreateAssetApiResponse);
    handleCreditsChange(nextCredits);

    return asset;
  }

  // The one place asset edits get persisted — called from the Composer's
  // Save button. No credit deduction here (that's create-time, and per
  // the parked product question, arguably shouldn't even be there).
  async function handleUpdateAsset(brandId, assetId, content) {
    return updateAsset(
      configArray,
      { brandId, assetId, content },
      setIsSavingAsset,
      setSaveAssetApiResponse
    );
  }

  // Only build configArray once we actually have both pieces — this used to
  // be safe because every branch above it returned early; now that Router
  // wraps a single return, it has to be guarded explicitly instead.
  let configArray = null;
  if (isAuthenticated && userConfig && configResponse) {
    const {
      userEmail,
      userName,
      userNickname,
      userRole,
      support_id,
      support_key,
      patternAPI,
      clientId,
      testingState
    } = userConfig;

    const {
      config: brandConfig,
      credits,
      current_storage: currentStorage,
      storage_limit: storageLimit,
      plan: clientPlan,
      client_name,
    } = configResponse.data.body;

    configArray = {
      userEmail,
      userName,
      userNickname,
      userRole,
      support_id,
      support_key,
      patternAPI,
      config: brandConfig,
      credits,
      currentStorage,
      storageLimit,
      clientPlan,
      client_name,
      clientId,
      testingState
    };
  }

  return (
    <Router>
      {showLoader ? (
        <Loader isLoading={true} />
      ) : !isAuthenticated ? (
        <Routes>
          <Route path="/" element={<LandingPage />} />
        </Routes>
      ) : configError ? (
        <div>Something went wrong loading your workspace. Please refresh.</div>
      ) : (
        <Routes>
          <Route path="/" element={<Home config={configArray} />}>
            <Route index element={<Navigate to="/brands" replace />} />
            <Route path="brands" element={<Brands brandConfig={configArray.config} role={configArray.userRole} config={configArray} />} />
            <Route
              path="brands/new"
              element={
                <NewBrand
                  config={configArray}
                  onCreateBrand={handleCreateBrand}
                  isSavingBrand={isSavingBrand}
                />
              }
            />
            <Route
              path="brands/:slug"
              element={
                <BrandDetail
                  config={configArray}
                  onSaveBrand={handleSaveBrand}
                  isSavingBrand={isSavingBrand}
                  onSaveReferences={handleSaveReferences}
                  isSavingReferences={isSavingReferences}
                  onSaveOffers={handleSaveOffers}
                  isSavingOffers={isSavingOffers}
                  onDeleteBrand={handleDeleteBrand}
                />
              }
            />
            <Route path="assets" element={<Assets config={configArray} />} />
            <Route path="usage" element={<Usage config={configArray} />} />
            <Route
              path="generate"
              element={
                <Generate
                  config={configArray}
                  onSaveOffers={handleSaveOffers}
                  onCreateAsset={handleCreateAsset}
                  isCreatingAsset={isCreatingAsset}
                />
              }
            />
            <Route
              path="compose/:assetId"
              element={
                <Compose
                  config={configArray}
                  onUpdateAsset={handleUpdateAsset}
                  isSavingAsset={isSavingAsset}
                  onSaveReferences={handleSaveReferences}
                />
              }
            />
          </Route>
        </Routes>
      )}
    </Router>
  );
}

export default App;